import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Building2, CalendarDays, ShoppingCart, FileText, BarChart3, Coins, Landmark, ChevronDown, FileCheck2, Camera, Images, X, BellRing, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/common";
import { UploadImages } from "@/components/PendingUploads";
import { papi } from "@/lib/portalApi";
import { formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";

const POS = {
  payable: { label: "Net VAT Payable to Government (ZATCA)", short: "VAT payable", tone: "bg-red-50 border-red-200 text-red-700", action: "Mark as Paid", done: "Paid" },
  refundable: { label: "Net VAT Refundable / Credit from ZATCA", short: "VAT refundable", tone: "bg-emerald-50 border-emerald-200 text-emerald-700", action: "Mark Refund Received", done: "Refund received" },
  nil: { label: "Net VAT Position: Nil", short: "Nil return", tone: "bg-slate-50 border-slate-200 text-slate-700", action: "Mark as Filed", done: "Filed" },
};
const sar = (v) => `SAR ${fmtMoney(v)}`;
const period = (r) => `${fmtDate(r.date_from)} – ${fmtDate(r.date_to)}`;

const Tile = ({ icon: I, label, value, tone, testid }) => (
  <div className={`rounded-2xl p-2.5 flex gap-2 items-center ${tone}`} data-testid={testid}>
    <div className="w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center shrink-0"><I className="w-4 h-4" /></div>
    <div className="min-w-0"><div className="text-[10px] leading-tight text-slate-600">{label}</div><div className="num font-semibold text-[13px] text-slate-900 whitespace-nowrap">{sar(value)}</div></div>
  </div>
);

const Section = ({ icon: I, title, count, children, testid }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-3" data-testid={`${testid}-toggle`}>
        <I className="w-5 h-5 text-emerald-700" /><span className="font-semibold">{title}</span><span className="text-sm text-slate-400">· {count} record{count === 1 ? "" : "s"}</span>
        <ChevronDown className={`w-5 h-5 ml-auto text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{count ? children : <div className="text-center text-sm text-slate-400 py-4">No entries</div>}</div>}
    </div>
  );
};

const KV = ({ k, v, strong }) => (
  <div className={`flex justify-between gap-2 text-[13px] ${strong ? "font-semibold bg-emerald-50 rounded-lg px-2 py-1 -mx-2" : ""}`}><span className="text-slate-500">{k}</span><span className="num text-right text-slate-900 break-all">{v}</span></div>
);

const PurchaseCard = ({ b }) => (
  <div className="border border-slate-200 rounded-xl p-3">
    <div className="flex justify-between items-center mb-2 gap-2"><span className="num font-semibold text-sky-800 whitespace-nowrap">{b.record_no}</span><span className="num text-xs text-slate-500">{fmtDate(b.invoice_date)} · Inv {b.invoice_number}</span></div>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
      <div className="space-y-1"><KV k="Vendor" v={b.vendor_name} /><KV k="Vendor VAT No." v={b.vendor_vat_number} /><KV k="Item" v={b.item_name} /></div>
      <div className="space-y-1"><KV k="Before VAT" v={sar(b.before_vat)} /><KV k="VAT amount" v={sar(b.vat_paid)} /><KV k="Total" v={sar(b.total)} strong /></div>
    </div>
  </div>
);

const SaleCard = ({ s }) => (
  <div className="border border-slate-200 rounded-xl p-3">
    <div className="flex justify-between items-center mb-2 gap-2"><span className="num font-semibold text-sky-800 whitespace-nowrap">{s.record_no}</span><span className="num text-xs text-slate-500">{fmtDate(s.period_from)} – {fmtDate(s.period_to)} · VAT {s.vat_rate}%</span></div>
    <div className="grid grid-cols-3 gap-2 text-center">
      {[["Total (incl. VAT)", s.total_sales], ["VAT amount", s.vat_amount], ["Net sales", s.net_sales]].map(([k, v]) => <div key={k}><div className="text-[11px] text-slate-500">{k}</div><div className="num font-semibold text-sm">{fmtMoney(v)}</div></div>)}
    </div>
    {s.notes && <div className="mt-2 text-xs bg-sky-50 rounded-lg px-2 py-1.5 text-slate-600">Notes: {s.notes}</div>}
  </div>
);

const StatusForm = ({ r, onSaved, onCancel }) => {
  const [remarks, setRemarks] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const cam = useRef(null);
  const gal = useRef(null);
  const add = (list) => { const picked = Array.from(list).map((file) => ({ file, url: URL.createObjectURL(file) })); setFiles((f) => [...f, ...picked].slice(0, 5)); };
  const save = async () => {
    const fd = new FormData();
    fd.append("remarks", remarks);
    files.forEach((f) => fd.append("files", f.file));
    setBusy(true);
    try { const { data } = await papi.post(`/portal/vat-reports/${r.id}/status`, fd); toast.success(`Marked as ${POS[r.position].done}`); onSaved(data); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3" data-testid="vat-status-form">
      <div className="font-semibold">{POS[r.position].action}</div>
      <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (e.g. paid via SADAD on 20/11/2026, ref no.)" className="min-h-[90px]" data-testid="vat-status-remarks" />
      <div className="text-xs text-slate-500">{r.position === "payable" ? "Payment challan / receipt" : "Supporting document"} (optional)</div>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => cam.current.click()} className="rounded-xl border border-emerald-200 py-3 flex items-center justify-center gap-2 text-emerald-900 text-sm font-medium active:scale-95 transition-transform" data-testid="vat-challan-camera"><Camera className="w-4 h-4" />Take photo</button>
        <button onClick={() => gal.current.click()} className="rounded-xl border border-emerald-200 py-3 flex items-center justify-center gap-2 text-emerald-900 text-sm font-medium active:scale-95 transition-transform" data-testid="vat-challan-gallery"><Images className="w-4 h-4" />Gallery</button>
        <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="vat-challan-camera-input" />
        <input ref={gal} type="file" accept="image/*" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="vat-challan-gallery-input" />
      </div>
      {files.length > 0 && <div className="flex gap-2 overflow-x-auto">{files.map((f, i) => (
        <div key={f.url} className="relative w-16 h-16 rounded-lg overflow-hidden shrink-0"><img src={f.url} alt="" className="w-full h-full object-cover" />
          <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`vat-challan-remove-${i}`}><X className="w-3 h-3" /></button></div>
      ))}</div>}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" className="h-11 rounded-xl" onClick={onCancel} data-testid="vat-status-cancel">Cancel</Button>
        <Button disabled={busy || remarks.trim().length < 2} onClick={save} className="h-11 rounded-xl bg-emerald-800 hover:bg-emerald-900" data-testid="vat-status-save">{busy ? "Saving…" : "Confirm"}</Button>
      </div>
    </div>
  );
};

const StatusBlock = ({ r, onSaved }) => {
  const [form, setForm] = useState(false);
  const [view, setView] = useState(false);
  if (r.status === "pending") {
    return form ? <StatusForm r={r} onSaved={onSaved} onCancel={() => setForm(false)} /> : (
      <Button onClick={() => setForm(true)} className="w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-600 text-[#064E3B] font-semibold" data-testid="vat-status-open"><FileCheck2 className="w-4 h-4 mr-2" />{POS[r.position].action}</Button>
    );
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 space-y-2" data-testid="vat-status-done">
      <div className="flex justify-between items-center"><span className="font-semibold">Status</span><StatusBadge status={r.status} /></div>
      {r.status_at && <div className="text-xs text-slate-500 num">Updated {fmtDateTime(r.status_at)}</div>}
      <div className="text-sm text-slate-700">{r.remarks}</div>
      {r.challan.length > 0 && <div className="flex gap-2">{r.challan.map((im) => <button key={im.thumb_path} onClick={() => setView(true)} data-testid="vat-challan-thumb"><img src={im.thumb_url} alt="" className="w-16 h-16 rounded-lg object-cover" /></button>)}</div>}
      {view && (
        <div className="fixed inset-0 z-[70] bg-black flex flex-col" data-testid="vat-challan-viewer">
          <div className="flex justify-start p-3 text-white"><button onClick={() => setView(false)} data-testid="vat-challan-viewer-close"><X className="w-6 h-6" /></button></div>
          <div className="flex-1 p-2"><UploadImages upload={{ id: r.id, images: r.challan }} className="h-[78vh]" testid="vat-challan-zoom" /></div>
        </div>
      )}
    </div>
  );
};

export const ReportSheet = ({ id, onClose, onChanged }) => {
  const [r, setR] = useState(null);
  useEffect(() => { papi.get(`/portal/vat-reports/${id}`).then(({ data }) => setR(data)).catch((e) => { toast.error(formatErr(e)); onClose(); }); }, [id, onClose]);
  const s = r?.summary;
  const p = r && POS[r.position];
  return (
    <div className="fixed inset-0 z-[60] bg-slate-100 overflow-y-auto" data-testid="vat-report-sheet">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 flex items-center gap-2 px-3 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3">
        <button onClick={onClose} className="p-2 rounded-full active:bg-slate-100" data-testid="vat-report-back"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 text-center font-semibold text-lg -ml-9 pointer-events-none">VAT Consolidation</div>
      </div>
      {!r ? <div className="text-center text-slate-400 py-20">Loading…</div> : (
        <div className="max-w-2xl mx-auto p-3 space-y-3 pb-10">
          <div className="bg-white rounded-2xl shadow-sm p-4 flex gap-3">
            <div className="w-14 h-14 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center shrink-0"><Building2 className="w-7 h-7" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex justify-between items-start gap-2"><div className="font-semibold text-lg leading-tight" data-testid="vat-report-company">{r.client_name} <span className="num text-sm text-slate-500">({r.client_code})</span></div><StatusBadge status={r.status} testid="vat-report-status" /></div>
              <div className="text-sm num text-slate-600">VAT No. {r.client_vat_number}</div>
              <div className="text-sm num flex items-center gap-1.5 mt-1"><CalendarDays className="w-4 h-4 text-slate-400" /><span data-testid="vat-report-period">{period(r)}</span>{r.period_label && <span className="text-xs text-slate-500">· {r.period_label}</span>}</div>
              <div className="text-[11px] text-slate-500 mt-1">{r.report_no} · Prepared by {r.shared_by_name} · <span className="num">{fmtDateTime(r.shared_at)}</span></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-3 space-y-3">
            <div className="flex justify-between items-center px-1"><span className="font-semibold">Summary</span><span className="text-xs text-slate-500">Amount ({r.currency})</span></div>
            <div className="grid grid-cols-2 gap-2">
              <Tile icon={ShoppingCart} label="Total purchase value (incl. VAT)" value={s.purchase_total} tone="bg-sky-50 text-sky-700" testid="vat-tile-purchase" />
              <Tile icon={FileText} label="Purchase VAT (Input VAT)" value={s.purchase_vat} tone="bg-emerald-50 text-emerald-700" testid="vat-tile-purchase-vat" />
              <Tile icon={BarChart3} label="Total sales (incl. VAT)" value={s.sales_total} tone="bg-indigo-50 text-indigo-700" testid="vat-tile-sales" />
              <Tile icon={Coins} label="Sales VAT (Output VAT)" value={s.sales_vat} tone="bg-amber-50 text-amber-700" testid="vat-tile-sales-vat" />
            </div>
            <div className={`rounded-2xl border p-3 flex items-center gap-3 ${p.tone}`} data-testid="vat-report-net">
              <Landmark className="w-6 h-6 shrink-0" /><span className="flex-1 text-sm font-semibold">{p.label}</span><span className="num text-xl font-bold">{sar(Math.abs(s.net_vat))}</span>
            </div>
          </div>
          <Section icon={ShoppingCart} title="Purchases" count={r.purchases.length} testid="vat-report-purchases">{r.purchases.map((b) => <PurchaseCard key={b.record_no} b={b} />)}</Section>
          <Section icon={BarChart3} title="Sales" count={r.sales.length} testid="vat-report-sales">{r.sales.map((x) => <SaleCard key={x.record_no} s={x} />)}</Section>
          <StatusBlock r={r} onSaved={(d) => { setR(d); onChanged(); }} />
        </div>
      )}
    </div>
  );
};

const ReportRow = ({ r, onOpen, i }) => (
  <button onClick={() => onOpen(r.id)} className="w-full text-left bg-white rounded-2xl p-4 shadow-sm active:scale-[0.99] transition-transform" data-testid={`vat-report-row-${i}`}>
    <div className="flex justify-between items-start gap-2">
      <div><div className="num font-semibold">{period(r)}</div><div className="text-[11px] text-slate-400 num">{r.report_no} · Shared {fmtDateTime(r.shared_at)}</div></div>
      <StatusBadge status={r.status} />
    </div>
    <div className="flex justify-between items-end mt-2">
      <span className="text-xs text-slate-500">{POS[r.position].short}</span>
      <span className={`num font-semibold ${r.position === "payable" ? "text-red-700" : "text-emerald-700"}`}>{sar(Math.abs(r.summary.net_vat))}</span>
    </div>
  </button>
);

export const VatReportsTab = ({ tick, onOpen }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (skip) => {
    setBusy(true);
    try {
      const { data } = await papi.get("/portal/vat-reports", { params: { skip, limit: 12 } });
      setRows((prev) => (skip ? [...prev, ...data.items] : data.items)); setTotal(data.total);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(0); }, [load, tick]);
  return (
    <div className="space-y-3" data-testid="portal-vat-tab">
      {!rows.length && !busy && <div className="text-center text-sm text-slate-400 py-10" data-testid="vat-reports-empty">No VAT reports shared yet</div>}
      {rows.map((r, i) => <ReportRow key={r.id} r={r} i={i} onOpen={onOpen} />)}
      {rows.length < total && <Button variant="outline" disabled={busy} onClick={() => load(rows.length)} className="w-full h-11 rounded-xl bg-white" data-testid="vat-reports-load-more">{busy ? "Loading…" : `Load more (${total - rows.length} more)`}</Button>}
    </div>
  );
};

export const VatAlerts = ({ pending, onOpen, onAll }) => (
  <div className="space-y-2 mb-4" data-testid="portal-vat-alerts">
    {pending.slice(0, 2).map((r, i) => (
      <button key={r.id} onClick={() => onOpen(r.id)} className={`w-full text-left rounded-2xl border p-3 flex items-center gap-3 shadow-sm ${POS[r.position].tone}`} data-testid={`portal-vat-alert-${i}`}>
        <BellRing className="w-5 h-5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">{POS[r.position].short} · {sar(Math.abs(r.summary.net_vat))}</div>
          <div className="text-[11px] num opacity-80">{period(r)} · Pending — tap to view</div>
        </div>
        <ChevronRight className="w-4 h-4" />
      </button>
    ))}
    {pending.length > 2 && <button onClick={onAll} className="w-full text-center text-sm font-medium text-emerald-800 py-1" data-testid="portal-vat-alerts-all">View all {pending.length} pending reports</button>}
  </div>
);
